<template>
  <div class="p-2 sm:p-3">
    <div class="flex justify-center flex-wrap">
      <div class="min-w-full sm:min-w-30 max-w-5xl m-2 flex-1">
        <div class="bg-white rounded-lg shadow-card p-4 sm:p-12">
          <h1 class="font-semibold text-2xl lg:text-3xl text-center">
            Review Menu
          </h1>
          <section class="mt-12">
            <div v-if="progressLoading" class="text-center text-gray-600 my-2 p-5">
              <Spinner class="mx-auto" orange />
            </div>
            <div v-else-if="categories.length === 0" class="text-center text-gray-600 my-2 p-5"></div>
            <div v-else>
              <div v-for="(category, index) in categories" :key="category.uuid"
                   :class="{'border-b border-gray-300': index < categories.length - 1}"
                   class="py-5">
                <div class="flex flex-row items-center px-2 md:px-4 cursor-pointer group">
                  <div
                    class="w-12 h-12 rounded bg-gray-600 overflow-hidden flex items-center justify-center flex-shrink-0">
                    <img :src="category.image" alt="Order Details Page" />
                  </div>
                  <div class="ml-4 min-w-0">
                    <div class="flex items-center">
                      <div class="font-semibold text-primary">
                        {{ category.name }}
                      </div>
                      <button class="px-2 py-1 ml-2 invisible group-hover:visible flex-shrink-0"
                              @click="onClickEditCategory(category)">
                        <img src="@/assets/icons/edit-pencil.svg" alt="BuildaBox Order Details Page" />
                      </button>
                    </div>
                    <div class="text-gray-600 mt-1">{{ category.description }}</div>
                  </div>
                </div>
                <div class="mt-4 md:ml-10">
                  <div v-if="category.items && category.items.length > 0" class="">
                    <draggable v-model="category.items" handle=".draggable-handler"
                               @update="onUpdateSort(category, category.items)"
                               :draggable="'.line-item'">
                      <div v-for="item in category.items" :key="item.uuid" class="line-item">
                        <div class="flex flex-row items-center py-4 cursor-pointer group">
                          <button class="btn ml-3 draggable-handler mr-4 flex-shrink-0">
                            <img src="@/assets/icons/ic_drag.svg" alt="BuildaBox Order Process" />
                          </button>
                          <div
                            class="w-12 h-12 rounded bg-gray-600 overflow-hidden flex items-center justify-center flex-shrink-0">
                            <img :src="item.image" alt="BuildaBox Order Details Page" />
                          </div>
                          <div class="ml-4 min-w-0">
                            <div class="flex items-center">
                              <div class="font-semibold ">
                                {{ item.name }}
                              </div>
                              <button class="px-2 py-1 ml-2 invisible group-hover:visible flex-shrink-0"
                                      @click="onClickEditItem(item)">
                                <img src="@/assets/icons/edit-pencil.svg" alt="BuildaBox Order Details Page" />
                              </button>
                            </div>
                            <div class="text-gray-600 mt-1">{{ item.description }}</div>
                          </div>
                        </div>
                      </div>
                    </draggable>
                  </div>
                  <div v-else class="text-center text-gray-600 my-2 p-5"></div>
                </div>
              </div>
            </div>
          </section>
          <section class="mt-16">
            <div class="flex flex-col-reverse sm:flex-row justify-between">
              <button
                class="btn btn-secondary font-semibold rounded-lg px-10 min-w-10 mt-5 sm:mt-0"
                @click="$emit('back')"
              >
                <span>Back</span>
              </button>
              <button
                class="btn btn-primary font-semibold rounded-lg px-10 min-w-10"
                :disabled="!canProceedToNextStep"
                @click="submit"
              >
                <span>Next</span>
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
    <Modal v-if="selectedCategory">
      <div slot="modal-wrapper">
        <div class="flex justify-center p-2">
          <div class="bg-white rounded-lg shadow-card px-4 py-8 sm:p-12 min-w-full sm:min-w-30 max-w-3xl">
            <CatererCategoryEditor
              :caterer-id="caterer.id"
              :category="selectedCategory"
              @submit="selectedCategory = ''; loadCategories()"
              @cancel="selectedCategory = ''"
            />
          </div>
        </div>
      </div>
    </Modal>
    <Modal v-if="selectedItem">
      <div slot="modal-wrapper">
        <div class="flex justify-center p-2">
          <div class="bg-white rounded-lg shadow-card px-4 py-8 sm:p-12 min-w-full sm:min-w-30 max-w-3xl">
            <CatererLineItemEditor
              :caterer="caterer"
              :categories="categories"
              :line-item="selectedItem"
              @submit="selectedItem = ''; loadCategories()"
              @cancel="selectedItem = ''"
            />
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
  import draggable from "vuedraggable"
  import Modal from "@/components/common/Modal"
  import CatererCategoryEditor from "@/components/CatererCategoryEditor";
  import CatererLineItemEditor from "@/components/CatererLineItemEditor";
  import Spinner from "@/components/common/Spinner"
  import Api from "@/api/api_v1.0"
  import {mapGetters} from "vuex";

  export default {
    components: {
      Spinner,
      CatererLineItemEditor,
      CatererCategoryEditor,
      Modal,
      draggable
    },
    data() {
      return {
        progressLoading: false,
        categories: [],
        selectedCategory: '',
        selectedItem: ''
      }
    },
    computed: {
      ...mapGetters(['isAuthenticated', 'personalDetails']),

      caterer() {
        return this.$store.state.catererDetails;
      },
      canProceedToNextStep() {
        return this.categories && this.categories.length > 0
      },
    },
    mounted() {
      this.loadCategories()
    },
    beforeCreate() {
      this.$root.$on("catererTrySubmitStep", () => { this.$emit('nextButtonPressed') });
    },
    beforeDestroy() {
      this.$root.$off("catererTrySubmitStep", () => { this.$emit('nextButtonPressed') });
    },
    methods: {
      loadCategories() {
        this.progressLoading = true
        Api.getCatererCategories(this.caterer.id).then(categories => {
          this.progressLoading = false
          this.categories = categories.data.map(category => {
            category.isSelected = false
            category.file = ''
            category.items = category.items.map(item => {
              item.isSelected = false
              item.file = ''
              return item
            })
            return category
          })
        }).catch(() => {
          this.progressLoading = false
        })
      },
      onClickEditCategory(category) {
        this.selectedCategory = JSON.parse(JSON.stringify(category))
      },
      onClickEditItem(item) {
        this.selectedItem = JSON.parse(JSON.stringify(item))
      },
      onUpdateSort(category, items) {
        const ids = items.map(item => item.id)
        Api.sortCatererCategoryItems(this.caterer.id, category.id, ids)
      },

      async submit() {
        this.$emit('nextButtonPressed');
      }
    }
  }
</script>